/**
 * Loop puro de firma masiva (F1D-5, #103, ADR-036): consume digests del server, los firma en el
 * daemon (1 sesión de token) y devuelve los CMS al server, hasta que el servidor indique
 * {@code batchDone === true}. Sin React ni estado global — así es testeable de punta a punta con
 * mocks.
 *
 * Flujo del loop:
 *   1. nextChunk devuelve { chunkDigests, remaining, preparing, batchDone, ingestState }.
 *   2. Si chunkDigests vacío y batchDone=false → el ingestor/preparador sigue trabajando;
 *      esperar POLL_MS y reintentar (sin llamar al daemon).
 *   3. Si chunkDigests vacío y batchDone=true → terminó; salir.
 *   4. Si hay digests, firmarlos en el daemon, mapear cada CMS al deliveryToken de su digest
 *      (por docId), y enviar a submitChunk con { docId, cmsBase64, deliveryToken }.
 *      CRÍTICO: si un resultado del daemon no tiene deliveryToken conocido, se OMITE (no se
 *      envía al server) y se registra un warning — evita descarte silencioso por token undefined.
 *
 * Aislamiento: un digest que falla en el daemon (failures[]) se registra en failedIds y se filtra
 * en las próximas pasadas, evitando re-firmar infinito un doc que siempre falla. Además (#103.2
 * Task G) esas fallas se REPORTAN al server en el submitChunk del mismo chunk ({signatures,
 * failures}) — el server las marca FAILED(DAEMON_*) y los docs dejan de quedar PREPARED fantasma;
 * si el chunk no produjo ninguna firma pero sí fallas, se submitea igual (solo-failures).
 *
 * Throttle (chunkDelayMs): algunos tokens/middleware PKCS#11 fallan cuando se les piden muchas
 * firmas seguidas (ráfaga). Con {@code chunkDelayMs > 0} se espera ese tiempo ENTRE chunks (sólo
 * si quedan docs por firmar; nunca una pausa colgada al final). Combinado con {@code chunkSize: 1}
 * da "una firma cada N ms", dándole aire al token entre operaciones.
 *
 * Espera fase-consciente (#103.2 Task E'): mientras no hay nada para firmar, el runner NO cuenta
 * polls a ciegas — mira la `phase` que reporta el server (delivering|preparing|assembling|
 * waiting_lease|ingesting). Con fase activa el server ESTÁ trabajando: se espera con backoff
 * (1.5s→5s) sin límite, porque con el recovery scanner (Task F) la espera siempre converge, y se
 * reporta la fase real por onProgress para que el modal diga la verdad. El presupuesto
 * (idleBudgetMs, 10 min) sólo corre cuando NO hay fase activa NI progreso — el único caso en que
 * rendirse es honesto; el error que se tira es específico y reanudable (kind='batch_stalled',
 * resumable=true). Antes: 90 s a ciegas y un genérico "El servidor tardó demasiado" mientras el
 * server preparaba/ensamblaba tranquilo (freeze del lote 19).
 *
 * Progreso = se firmó y envió al menos 1 doc, o remaining BAJÓ (#103.1: el servidor ensambla; lotes
 * de 130k tardan minutos/horas). Cualquiera de los dos resetea presupuesto y backoff.
 *
 * @param {{batchId:number, pin:string, certAlias:string, chunkSize:number,
 *          api:{nextChunk:Function, submitChunk:Function}, daemonSignBatch:Function,
 *          onProgress?:Function, chunkDelayMs?:number, retryFailed?:boolean,
 *          sleep?:Function, idleBudgetMs?:number}} args
 * @returns {Promise<{failures:Array<{docId:number,error:string}>}>}
 */

/** Espera inicial (ms) entre polls de nextChunk cuando no hay nada para firmar todavía. */
const POLL_MS = 1500;

/** Techo del backoff de espera (ms): con fase activa se poletea a lo sumo cada 5 s. */
const POLL_MAX_MS = 5000;

/** Factor de backoff entre polls de espera consecutivos (1500 → 2250 → 3375 → 5000). */
const POLL_BACKOFF = 1.5;

/**
 * Presupuesto de espera (ms) SIN fase activa ni progreso antes de rendirse. 10 min: largo a
 * propósito — con el recovery scanner del server (#103.2 Task F, cada 60s) cualquier lote atascado
 * se destraba solo, así que rendirse antes sería rendirse justo cuando el server iba a curarse.
 */
const IDLE_BUDGET_MS = 10 * 60 * 1000;

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function runBatchLoop({
  batchId, pin, certAlias, chunkSize, api, daemonSignBatch, onProgress,
  chunkDelayMs = 0, sleep = defaultSleep, retryFailed = false,
  idleBudgetMs = IDLE_BUDGET_MS,
}) {
  const failures = [];
  const attempted = new Set(); // TODOS los docId ya intentados en esta pasada
  let firstIteration = true;
  let idleMs = 0;        // ms de espera acumulados SIN fase activa ni progreso (contra idleBudgetMs)
  let pollMs = POLL_MS;  // espera actual entre polls (backoff hasta POLL_MAX_MS)
  let lastRemaining = Infinity; // último remaining visto: si baja, el servidor avanzó (#103.1)

  /**
   * Una iteración de espera: decide si rendirse, informa la fase real y duerme con backoff.
   * Tira kind='batch_stalled' sólo si se agotó el presupuesto sin fase activa.
   */
  const waitForServer = async (phase, remaining, progressed) => {
    if (phase || progressed) {
      // El server reporta trabajo en curso (o el lote avanzó en este mismo poll) → esperar no es
      // colgarse. No consume presupuesto, por chico que sea.
      idleMs = 0;
    } else {
      idleMs += pollMs;
      if (idleMs > idleBudgetMs) {
        // Mensaje honesto SIN la palabra "timeout" (useBatchSignature la mal-clasificaba como
        // "El servidor tardó demasiado", #103.1) y reanudable: el lote sigue vivo server-side.
        const stallErr = new Error('El servidor no reporta actividad en este lote y no avanza. Podés reintentar: los documentos ya firmados no se reprocesan.');
        stallErr.kind = 'batch_stalled';
        stallErr.resumable = true;
        throw stallErr;
      }
    }
    onProgress?.({ signedThisChunk: 0, remaining, phase: phase ?? null, waiting: true });
    await sleep(pollMs);
    pollMs = Math.min(Math.round(pollMs * POLL_BACKOFF), POLL_MAX_MS);
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const nc = await api.nextChunk(batchId, chunkSize, firstIteration ? retryFailed : false);
    firstIteration = false;

    const digests = nc?.chunkDigests || [];
    const remaining = nc?.remaining ?? 0;
    const batchDone = nc?.batchDone === true;
    // #103.1: preparing = el server sigue ingiriendo/preparando digests (pending>0 || !ingested).
    // Cuando NO hay digests y NO se está preparando más, la FIRMA terminó (los remanentes están
    // SIGNED/ensamblándose) → el runner sale y deja la espera del ensamblado a pollUntilDone.
    const preparing = nc?.preparing === true;
    // #103.2 Task E': fase operativa del server (null = nada pendiente). Clientes contra un server
    // viejo la reciben undefined → se comportan como "sin fase activa" (rige el presupuesto).
    const phase = nc?.phase ?? null;

    // #103.1: durante el ensamblado server-side de lotes grandes (130k), el cliente hace next-chunk
    // en vacío mientras el servidor ENSAMBLA. remaining (pending+live) baja a medida que el servidor
    // firma/ensambla → si bajó, HAY progreso: resetear presupuesto y backoff (volver a poletear
    // seguido, que es cuando conviene). Sólo corre el presupuesto si remaining queda ESTANCADO.
    const progressed = remaining < lastRemaining;
    if (progressed) { idleMs = 0; pollMs = POLL_MS; }
    lastRemaining = remaining;

    // Si no hay digests listos: si el lote terminó, salimos; si no, el servidor está
    // preparando/ingiriendo → esperar y reintentar SIN llamar al daemon.
    if (digests.length === 0) {
      if (batchDone) break;
      // #103.1: Firma completa: no hay digests para firmar y el server no está preparando más
      // (todos los remanentes están SIGNED/ensamblándose). Salir y dejar la espera del
      // ensamblado a pollUntilDone (que muestra "Ensamblando X/total" y no se rinde).
      if (!preparing) break;
      await waitForServer(phase, remaining, progressed);
      continue;
    }

    // Filtrar los ya intentados. CRÍTICO: un doc rechazado server-side en el ensamble
    // (ej. signature_invalid por token que devolvió una firma corrupta) vuelve a 'pending' y
    // nextChunk lo re-entrega. Filtrar solo por fallas del daemon NO lo cubría → el loop
    // re-firmaba infinito y nunca terminaba (modal atascado en 'signing', incerrable).
    // Marcar cada doc como intentado (una sola vez por pasada) garantiza terminación; el
    // reintento (retry) es una pasada nueva de runBatchLoop, así que vuelve a intentarlos.
    const workable = digests.filter((d) => !attempted.has(d.docId));

    // Si todos los digests de este chunk ya fueron intentados, y el lote terminó → salimos.
    // Si aún no terminó, nextChunk traerá otros docs (o eventualmente batchDone=true).
    if (workable.length === 0) {
      if (batchDone) break;
      // #103.1: Firma completa: todos los digests de este chunk ya fueron intentados y el server
      // no está preparando más → salir y dejar la espera del ensamblado a pollUntilDone.
      if (!preparing) break;
      // Caso poco probable: todos intentados pero batchDone=false (servidor reenviando docs
      // que fallaron en ensamble y aún hay más). Misma espera fase-consciente que arriba.
      await waitForServer(phase, remaining, progressed);
      continue;
    }

    workable.forEach((d) => attempted.add(d.docId));

    // Mapa docId → deliveryToken del chunk actual: CRÍTICO para que cada CMS firmado
    // viaje con el token que lo habilita en el servidor (ADR-036, #103).
    const tokenByDoc = new Map(workable.map((d) => [d.docId, d.deliveryToken]));

    // Marcamos el origen del error: las llamadas al daemon (firma) vs las del servidor Bóveda
    // (nextChunk/submitChunk) fallan distinto y el mensaje al usuario debe ser honesto — no culpar
    // al "cliente local de firma" cuando en realidad falló el servidor (#98: un timeout de axios en
    // nextChunk se mostraba como "verificá que el cliente esté en ejecución", engañando).
    // CRÍTICO (#103): al daemon se le manda SOLO lo que su contrato declara. deliveryToken es un
    // concepto server-side (UI↔Bóveda) y enviarlo provoca UnrecognizedPropertyException
    // (Jackson FAIL_ON_UNKNOWN_PROPERTIES=true) → HTTP 500. Por eso los campos se listan a mano en
    // vez de reenviar el objeto entero: agregar uno es una decisión, no un descuido.
    // padesLevel se suma en #108 — el daemon lo acepta como opcional desde el cliente 0.1.13.
    const daemonDigests = workable.map(({ docId, messageDigestHex, signingDate, padesLevel }) => (
      // padesLevel sólo si el server lo mandó: contra un server anterior a #108 la clave ni aparece,
      // y el daemon la trata como ausente ⇒ B-B. Mandarla en `undefined` funcionaría igual (JSON la
      // omite), pero deja el contrato explícito en vez de depender del serializador.
      padesLevel
        ? { docId, messageDigestHex, signingDate, padesLevel }
        : { docId, messageDigestHex, signingDate }
    ));

    let sb;
    try {
      sb = await daemonSignBatch({ digests: daemonDigests, pin, certAlias });
    } catch (e) {
      if (e && typeof e === 'object') e.source = 'daemon';
      throw e;
    }

    // daemonSignBatch puede devolver:
    //   - Array<{docId,cmsBase64}>  (interfaz nueva, ADR-036)
    //   - {results:Array<{docId,cmsBase64}>, failures:Array<{docId,error}>}  (interfaz legacy)
    const results = Array.isArray(sb) ? sb : (sb?.results || []);
    const daemonFailures = Array.isArray(sb) ? [] : (sb?.failures || []);
    // El array local se mantiene para el modal (UX sin cambios); ADEMÁS se reportan al server en
    // el submit del chunk (#103.2 Task G) para que los docs fallidos no queden PREPARED fantasma.
    for (const f of daemonFailures) { failures.push(f); }
    const chunkFailures = daemonFailures.map((f) => ({ docId: f.docId, reason: f.error || 'ERROR' }));

    // CRÍTICO (#103, review Task 14): solo enviar firmas que tengan un deliveryToken válido.
    // Si el daemon devuelve un docId que no está en el mapa (desconocido, stale, o sin token),
    // omitir esa firma con un warning — no enviar token undefined que el server rechazaría
    // silenciosamente dejando el doc sin firmar.
    const payload = [];
    for (const r of results) {
      const token = tokenByDoc.get(r.docId);
      if (!token) {
        // eslint-disable-next-line no-console
        console.warn(`[batchSignatureRunner] firma sin deliveryToken para doc=${r.docId} — se omite (docId desconocido o sin token)`);
        continue;
      }
      payload.push({ docId: r.docId, cmsBase64: r.cmsBase64, deliveryToken: token });
    }

    // #103.2 Task G: el submit del chunk viaja con las fallas del daemon del MISMO chunk; si el
    // payload de firmas quedó vacío pero hubo fallas, se submitea igual (solo-failures) — el server
    // marca esos docs FAILED(DAEMON_*), libera locks/placeholders y los deja retryable.
    if (payload.length > 0 || chunkFailures.length > 0) {
      await api.submitChunk(batchId, payload, chunkFailures);
      if (payload.length > 0) {
        // Hubo progreso real (firmas persistidas): resetear presupuesto y backoff de espera.
        idleMs = 0;
        pollMs = POLL_MS;
      }
    }

    // #108: cuántos de este chunk llevaban sello de tiempo. El nivel lo decide la serie y lo
    // resuelve el server al preparar, así que ANTES de este punto la UI no tiene forma de saberlo:
    // el aviso de costo se muestra cuando el dato existe, no estimado de antemano.
    const selladosEnChunk = workable.filter((d) => d.padesLevel && d.padesLevel !== 'B-B').length;
    onProgress?.({
      signedThisChunk: results.length, remaining, phase, waiting: false, selladosEnChunk,
    });

    // Throttle entre firmas para no saturar el token: sólo si quedan docs por firmar (remaining>0),
    // así nunca hay una pausa colgada tras la última firma.
    if (chunkDelayMs > 0 && remaining > 0) {
      await sleep(chunkDelayMs);
    }
  }

  return { failures };
}
